import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./CurrentPaywall.module.scss"

/**
 * CurrentPaywall (`/current`) — an original, self-contained EXPIRATION paywall
 * for Private Photo Vault Premium. Paywall type rolled at random for this run:
 * EXPIRATION (shown after a Premium subscription lapses — a win-back).
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * Your memories are all still here, bright and whole. Nothing was ever lost.
 * What switched off when Premium lapsed is the PROTECTION around them — the live
 * circuit that keeps a private encrypted copy, syncs every device, watches for
 * break-ins and holds a safety net for deletes. On this screen that circuit is
 * DARK because a plug has slipped out of its socket. You RECONNECT the plug —
 * a warm pulse of current runs up the cord, the protection ring around your
 * memories energizes, and the four real guarantees latch back on with a click.
 * The pitch is not "or you'll lose everything"; it's "your photos are fine —
 * plug protection back in and keep your vault current."
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls Bot message this run was exactly `default` — the explicit
 * reset keyword — so NO standing style directive is active. This is a free
 * concept / full-divergence run: aesthetic and concept chosen autonomously.
 *
 * ── Divergence (see claude-paywalls/_ledger.md — 70+ prior concepts) ────────
 * Layout      : a single upright VAULT-VITRINE on a wall, its protection ring
 *               fed from a wall SOCKET by a coiled cord — not hero+card, console,
 *               calendar, arch, receipt, quilt, split-flap board, music box,
 *               lighthouse, hearth, tide or snow-globe.
 * Interaction : RECONNECT-THE-PLUG — seat the dangling plug into the socket
 *               (tap / press / drag) and current climbs the cord to re-energize
 *               the ring. Distinct from every prior mechanic; the expiration set
 *               already used flip-a-master-switch (Standby), haul-a-halyard
 *               (Beacon), wind-a-key (Mainspring), pull-a-lever (Keep Posted),
 *               drag-the-tide (Tideline), lift-a-cover (Homecoming). Nothing in
 *               the ledger plugs a cord into a socket.
 * Register    : matter-of-fact relief with a small electric spark of delight —
 *               the satisfying CLICK of plugging back in. A cool, energized note
 *               deliberately away from the warm fireside/keepsake cluster that
 *               dominates the expiration family.
 * Art dir     : cool twilight navy glass, brand blue as the live current, a warm
 *               amber spark on the prongs — clean and modern, not cozy.
 * Value frame : PROTECTION CONTINUITY / risk-reversal — "your memories were never
 *               at risk; renewing only switches their protection back on."
 * Hero motif  : a wall vault-vitrine of your photos whose protection ring is fed
 *               by a plug you push back into the socket.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * The four things that latch on are REAL Premium features (Cloud Vault encrypted
 * second copy, sync across every device, Break-In Report, 30-day Trash Recovery).
 * The photos are visibly present and unchanged the entire time — explicit copy
 * that nothing is moved, deleted or lost when Premium lapses, and that renewing
 * only turns protection back on. NO data-loss threat, NO countdown, NO fake
 * scarcity. "Save 79%" is real arithmetic from live product data. Yearly is the
 * featured, default, most prominent price; monthly is small and secondary.
 * Interior images are labelled Unsplash demo photos, not the user's library.
 */

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

// the memories inside the vitrine — bundled Unsplash demo photos (no egress).
// They are bright and whole in every state; nothing here ever dims or is lost.
type Frame = {
  src: string
  x: number
  y: number
  r: number
  w: number
  z: number
  video?: boolean
}
const FRAMES: Frame[] = [
  { src: "/recall/mountain-1.jpg", x: 30, y: 34, r: -7, w: 44, z: 2 },
  { src: "/recall/beach-1.jpg", x: 70, y: 32, r: 8, w: 44, z: 2 },
  { src: "/recall/coffee-1.jpg", x: 28, y: 66, r: -9, w: 44, z: 3 },
  { src: "/recall/dog-1.jpg", x: 72, y: 64, r: 10, w: 44, z: 3, video: true },
  { src: "/recall/sunset-1.jpg", x: 50, y: 50, r: 0, w: 52, z: 4 },
]

// the four real Premium guarantees — each latches on as the current climbs
const GUARDS = [
  {
    key: "backup",
    Icon: svg.featureIcons.CloudVault,
    name: "Cloud Vault",
    line: "a private, encrypted second copy",
  },
  {
    key: "devices",
    Icon: svg.featureIcons.Wifi,
    name: "Every device",
    line: "synced iPhone to iPad to Mac",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-In Report",
    line: "photo + map on a wrong PIN",
  },
  {
    key: "trash",
    Icon: svg.featureIcons.Trash,
    name: "Trash Recovery",
    line: "a 30-day net for deletes",
  },
]

type PlanKey = "yearly" | "monthly"

const CurrentPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // plugged → the plug is seated; lit → how many guards have latched (0..4);
  // live once the current has climbed and all four are on.
  const [plugged, setPlugged] = useState(false)
  const [lit, setLit] = useState(0)
  const live = plugged && lit >= GUARDS.length

  const litTimers = useRef<number[]>([])
  const userActed = useRef(false)

  const clearLit = useCallback(() => {
    litTimers.current.forEach((t) => clearTimeout(t))
    litTimers.current = []
  }, [])

  // reconnect: seat the plug, let the current climb the cord, then latch the
  // four guarantees on one by one. Re-running simply replays the surge.
  const reconnect = useCallback(() => {
    clearLit()
    setPlugged(true)
    setLit(0)
    for (let i = 1; i <= GUARDS.length; i++) {
      litTimers.current.push(
        window.setTimeout(
          () => setLit((n) => clamp(Math.max(n, i), 0, GUARDS.length)),
          520 + i * 300,
        ),
      )
    }
  }, [clearLit])

  const onReconnect = useCallback(() => {
    userActed.current = true
    reconnect()
  }, [reconnect])

  // auto-teach on mount: settle to the live, protected resting state we sell.
  // A real interaction from the user cancels the pending auto-run.
  useEffect(() => {
    if (reduceMotion) {
      setPlugged(true)
      setLit(GUARDS.length)
      return
    }
    const start = window.setTimeout(() => {
      if (!userActed.current) reconnect()
    }, 760)
    return () => {
      clearTimeout(start)
      clearLit()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  // dragging the plug toward the socket also seats it (once, per gesture)
  const dragged = useRef(false)
  const onPointerDown = useCallback(() => {
    dragged.current = false
  }, [])
  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (e.buttons === 0 || dragged.current) return
      dragged.current = true
      userActed.current = true
      reconnect()
    },
    [reconnect],
  )

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  return (
    <div
      className={`${styles.root} ${plugged ? styles.plugged : ""} ${
        live ? styles.live : ""
      }`}
    >
      <div className={styles.aura} aria-hidden="true" />

      {/* header — brand clarity in the first glance */}
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9 12.2l2.2 2.2L15 10.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.brandName}>
            Private Photo Vault
            <span className={styles.brandTag}>Premium</span>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={() => native.postNativeEvent("close")}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15">
            <path
              d="M6 6l12 12M18 6L6 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* intro */}
      <section className={styles.intro}>
        <span className={styles.kicker}>
          <span className={styles.kickerDot} aria-hidden="true" />
          {live ? "Premium reconnected" : "Premium lapsed"}
        </span>
        <h1 className={styles.title}>
          {live ? (
            <>
              Protection back on.
              <br />
              <span className={styles.titleAccent}>Your vault is current.</span>
            </>
          ) : (
            <>
              Your photos are all here.
              <br />
              <span className={styles.titleAccent}>
                Their protection switched off.
              </span>
            </>
          )}
        </h1>
        <p className={styles.sub}>
          {live
            ? "The circuit is live again — a private encrypted copy, synced across every device, watching over your memories."
            : "Nothing was moved, deleted or lost. Plug protection back in to re-arm the vault around your memories."}
        </p>
      </section>

      {/* ── the vault vitrine + its protection circuit ──────────────────── */}
      <section className={styles.stage}>
        <div className={styles.vitrineWrap}>
          {/* the energizing protection ring behind the glass */}
          <span className={styles.ring} aria-hidden="true">
            <svg viewBox="0 0 240 240" className={styles.ringSvg}>
              <circle className={styles.ringTrack} cx="120" cy="120" r="110" />
              <circle className={styles.ringLive} cx="120" cy="120" r="110" />
            </svg>
            {GUARDS.map((g, i) => {
              const on = lit > i
              const a = (-90 + i * 90) * (Math.PI / 180)
              return (
                <span
                  key={g.key}
                  className={`${styles.node} ${on ? styles.nodeOn : ""}`}
                  style={{
                    left: `${50 + Math.cos(a) * 46}%`,
                    top: `${50 + Math.sin(a) * 46}%`,
                  }}
                />
              )
            })}
          </span>

          {/* the vitrine holding the memories — bright and whole in every state */}
          <div
            className={styles.vitrine}
            role="button"
            tabIndex={0}
            onClick={onReconnect}
            aria-label={live ? "Protection is on" : "Reconnect protection"}
          >
            <span className={styles.glass} aria-hidden="true" />
            <span className={styles.frames} aria-hidden="true">
              {FRAMES.map((f) => (
                <span
                  key={f.src}
                  className={styles.frame}
                  style={{
                    left: `${f.x}%`,
                    top: `${f.y}%`,
                    width: `${f.w}%`,
                    ["--r" as any]: `${f.r}deg`,
                    zIndex: f.z,
                  }}
                >
                  <img src={f.src} alt="" draggable={false} />
                  {f.video && (
                    <span className={styles.play}>
                      <svg viewBox="0 0 24 24" width="12" height="12">
                        <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
                      </svg>
                    </span>
                  )}
                </span>
              ))}
            </span>
            {/* the seal badge on the glass */}
            <span className={styles.seal}>
              <span className={styles.sealLock} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="12" height="12">
                  <rect
                    x="5"
                    y="10.5"
                    width="14"
                    height="9.5"
                    rx="2"
                    fill="currentColor"
                  />
                  <path
                    d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                  />
                </svg>
              </span>
              {live ? "PROTECTED" : "UNPROTECTED"}
            </span>
          </div>
        </div>

        {/* the power connector: reconnect the plug → current climbs to the ring.
            Drawn as one SVG so the cord, plug, prongs and socket read cleanly. */}
        <div
          className={styles.power}
          role="button"
          tabIndex={0}
          onClick={onReconnect}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          aria-label={live ? "Protection connected" : "Reconnect the plug"}
        >
          <svg
            viewBox="0 0 220 108"
            className={styles.powerSvg}
            aria-hidden="true"
          >
            {/* cord from the vault (top) down into the plug */}
            <path
              className={styles.cordTrack}
              d="M110 2 C110 34 66 30 60 56"
              fill="none"
            />
            <path
              className={styles.cordLive}
              d="M110 2 C110 34 66 30 60 56"
              fill="none"
            />

            {/* the plug — slides right so its prongs seat under the socket.
                Drawn before the socket so the socket hides the inserted tips. */}
            <g className={styles.plugG}>
              <rect
                className={styles.plugBody}
                x="42"
                y="40"
                width="52"
                height="36"
                rx="11"
              />
              <rect
                className={styles.prong}
                x="94"
                y="48"
                width="22"
                height="6"
                rx="3"
              />
              <rect
                className={styles.prong}
                x="94"
                y="62"
                width="22"
                height="6"
                rx="3"
              />
            </g>

            {/* wall socket on the right (on top, so seated prongs read as inside) */}
            <g className={styles.socket}>
              <rect x="158" y="34" width="52" height="48" rx="13" />
              <rect
                className={styles.slot}
                x="176"
                y="45"
                width="5"
                height="26"
                rx="2.5"
              />
              <rect
                className={styles.slot}
                x="190"
                y="45"
                width="5"
                height="26"
                rx="2.5"
              />
            </g>

            {/* spark at the join on connection */}
            <circle className={styles.spark} cx="150" cy="58" r="6" />
          </svg>
        </div>

        <div className={styles.stageFoot}>
          <button
            type="button"
            className={styles.reconnectBtn}
            onClick={onReconnect}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
              <path
                d="M13 2L4.5 13.5H11l-1 8.5L19.5 10H13z"
                fill="currentColor"
              />
            </svg>
            {live ? "Reconnected" : "Reconnect protection"}
          </button>
          <span className={styles.progress} aria-live="polite">
            {live ? "Circuit live · 4 guarantees on" : `${lit}/4 re-arming`}
          </span>
        </div>
      </section>

      {/* ── the four real guarantees, latching as the current climbs ─────── */}
      <ul className={styles.guards}>
        {GUARDS.map((g, i) => {
          const on = lit > i
          const Icon = g.Icon
          return (
            <li
              key={g.key}
              className={`${styles.guard} ${on ? styles.guardOn : ""}`}
              style={{ ["--i" as any]: i }}
            >
              <span className={styles.guardIcon} aria-hidden="true">
                <Icon />
              </span>
              <span className={styles.guardText}>
                <span className={styles.guardName}>{g.name}</span>
                <span className={styles.guardLine}>{g.line}</span>
              </span>
              <span className={styles.guardState} aria-hidden="true">
                {on ? (
                  <svg viewBox="0 0 24 24" width="14" height="14">
                    <path
                      d="M5 12.6l4.3 4.3L19 7.4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <span className={styles.guardOff} />
                )}
              </span>
            </li>
          )
        })}
      </ul>

      {/* honest, compliant reassurance */}
      <p className={styles.assure}>
        <span className={styles.assureMark} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="13" height="13">
            <path
              d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        When Premium lapses your photos stay exactly where they are — nothing is
        moved or deleted. Renewing only switches their protection back on.
      </p>

      {/* ── PRICING: yearly featured, monthly small & secondary ──────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${plan === "yearly" ? styles.selected : ""}`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyGlow} aria-hidden="true" />
          <span className={styles.yearlyHead}>
            <span className={styles.yearlyName}>Yearly · keep it current</span>
            {savingsPct > 0 && (
              <span className={styles.saveTag}>Save {savingsPct}%</span>
            )}
          </span>
          <span className={styles.yearlyPriceRow}>
            <span className={styles.yearlyPrice}>{yearlyPrice}</span>
            <span className={styles.yearlyUnit}>/ year</span>
            <span className={styles.check} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M5 12.5l4.2 4.2L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.yearlyPerMo}>
            about {yearlyPerMonth}/mo, billed once a year
          </span>
        </button>

        <button
          className={`${styles.monthly} ${plan === "monthly" ? styles.monthlySel : ""}`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyName}>Monthly</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      <p className={styles.credit}>Demo photos from Unsplash</p>

      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? "Renew yearly — keep protection on"
              : "Renew monthly"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `${yearlyPrice}/yr · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>
      </div>
    </div>
  )
}

export default CurrentPaywall
